import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPriceZones, getPricingCatalog, PricingCatalogResult } from 'services/api/pay';
import { narrowPriceZone, PriceZoneCode } from '../priceZone';
import { logPricing, setPricingLogDefaults } from '../pricingLogger';

/**
 * 可购价区 + 默认价区决策。
 * zones 为 wire 结果经闭集过滤后的可购集合；defaultZone 命中可购集合才生效，否则取第一项。
 */
export const usePriceZones = (options?: { enabled?: boolean }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['pricing', 'price-zones'],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      try {
        const result = await getPriceZones();
        setPricingLogDefaults(result);
        logPricing('price_zones_loaded', { availableZones: result.priceZones?.map((v) => v.code) });
        return result;
      } catch (error) {
        logPricing('price_zones_failed', {}, error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  const zones = useMemo(
    () =>
      (data?.priceZones ?? [])
        .map((option) => narrowPriceZone(option.code))
        .filter((zone): zone is PriceZoneCode => !!zone),
    [data],
  );

  const defaultZone = useMemo(() => {
    const narrowed = narrowPriceZone(data?.defaultPriceZone);
    if (narrowed && zones.includes(narrowed)) {
      return narrowed;
    }
    return zones[0];
  }, [data, zones]);

  return {
    zones,
    defaultZone,
    defaultCurrency: data?.defaultCurrency,
    defaultCurrencySource: data?.defaultCurrencySource,
    isLoading,
  };
};

/** 按价区拉取套餐目录（queryKey 含价区，切区自动重拉）；
 * enabled 可延迟拉取（升级/续订目录对齐 perseus 打开抽屉才挂载即拉，避免进页面就发） */
export const usePricingCatalog = (
  zone: PriceZoneCode | undefined,
  orderAction: Pay.OrderType,
  options?: { enabled?: boolean },
) => {
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['pricing', 'catalog', zone, orderAction],
    queryFn: async () => {
      try {
        const result = await getPricingCatalog(zone!, orderAction);
        logPricing('catalog_loaded', {
          requestedPriceZone: zone,
          orderAction,
          priceZone: result.priceZone,
          catalogRevision: result.revision,
          recommendData: result.recommendData,
          packageCount: result.packages?.length ?? 0,
        });
        return result;
      } catch (error) {
        logPricing('catalog_failed', { priceZone: zone, orderAction }, error);
        throw error;
      }
    },
    enabled: !!zone && (options?.enabled ?? true),
  });

  return {
    catalog: data as PricingCatalogResult | undefined,
    // react-query v4 的 isLoading 即 status==='loading'：enabled:false 时无数据会恒为 true，
    // 与 isFetching 求交保证仅「无数据且请求中」为 loading
    isLoading: isLoading && isFetching,
    isError,
    refetch,
  };
};
